<template>
    <div class="slide-button-wrapper">
        <div class="slide-button"
            :class="{ 
                'slide-button--disabled': disabled,
                'slide-button--completed': isCompleted
            }">
            <!-- Main button container -->
            <div class="slide-button__container"
                ref="container"
                @touchstart="handleTouchStart"
                @touchmove="handleTouchMove"
                @touchend="handleTouchEnd"
                @mousedown="handleMouseDown"
                @mousemove="handleMouseMove"
                @mouseup="handleMouseUp"
                @mouseleave="handleMouseLeave">
                <!-- Left circular icon button -->
                <div class="slide-button__icon-wrapper"
                    :style="{ 
                        transform: `translateX(${sliderPosition}px)`,
                        transition: isDragging ? 'none' : ''
                    }"
                    ref="slider">
                    <div class="slide-button__icon">
                        <!-- Double chevron right icon -->
                        <svg viewBox="0 0 16 16" 
                            class="slide-button__chevron"
                            xmlns="http://www.w3.org/2000/svg">
                            <path d="M5.5 12.5L10 8L5.5 3.5L6.5 2.5L12 8L6.5 13.5L5.5 12.5Z"/>
                            <path d="M8.5 12.5L13 8L8.5 3.5L9.5 2.5L15 8L9.5 13.5L8.5 12.5Z"/>
                        </svg>
                    </div>
                </div>
                
                <!-- Success background overlay -->
                <div class="slide-button__success-overlay"
                    :style="{ 
                        width: isCompleted ? '100%' : `${Math.max(0, sliderPosition + (isDragging ? 45 : 0))}px`,
                        transition: isDragging ? 'none' : ''
                    }"
                ></div>
                
                <!-- Text label -->
                <div class="slide-button__text"
                    :class="{ 'slide-button__text--faded': sliderPosition > 50 }">
                    {{ isCompleted ? completedText : text }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "SlideToUseButton",
    props: {
        text: {
            type: String,
            default: "Slide to use now"
        },
        completedText: {
            type: String,
            default: "Completed!"
        },
        disabled: {
            type: Boolean,
            default: false
        },
        autoReset: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            isDragging: false,
            sliderPosition: 0,
            maxSlideDistance: 0,
            startX: 0,
            isCompleted: false,
            hasTriggered: false
        };
    },
    mounted() {
        this.calculateMaxSlideDistance();
        window.addEventListener('resize', this.calculateMaxSlideDistance);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.calculateMaxSlideDistance);
    },
    methods: {
        calculateMaxSlideDistance() {
            if (this.$refs.container && this.$refs.slider) {
                const containerWidth = this.$refs.container.offsetWidth;
                const sliderWidth = this.$refs.slider.offsetWidth;
                const padding = 16; // 8px padding on each side
                this.maxSlideDistance = containerWidth - sliderWidth - padding;
            }
        },

        // Touch events
        handleTouchStart(event) {
            if (this.disabled || this.isCompleted) return;
            this.startDrag(event.touches[0].clientX);
        },

        handleTouchMove(event) {
            if (!this.isDragging) return;
            event.preventDefault();
            this.updateSliderPosition(event.touches[0].clientX);
        },

        handleTouchEnd() {
            this.endDrag();
        },

        // Mouse events
        handleMouseDown(event) {
            if (this.disabled || this.isCompleted) return;
            this.startDrag(event.clientX);
        },

        handleMouseMove(event) {
            if (!this.isDragging) return;
            this.updateSliderPosition(event.clientX);
        },

        handleMouseUp() {
            this.endDrag();
        },

        handleMouseLeave() {
            this.endDrag();
        },

        // Drag logic
        startDrag(clientX) {
            this.isDragging = true;
            this.startX = clientX - this.sliderPosition;
            this.calculateMaxSlideDistance();
        },

        updateSliderPosition(clientX) {
            const newPosition = clientX - this.startX;
            this.sliderPosition = Math.max(0, Math.min(newPosition, this.maxSlideDistance));
        },

        endDrag() {
            if (!this.isDragging) return;
            this.isDragging = false;

            // Check if slider reached the end
            if (this.sliderPosition >= this.maxSlideDistance) {
                this.completeSlide();
            } else {
                this.resetSlider();
            }
        },

        completeSlide() {
            this.sliderPosition = this.maxSlideDistance;
            this.isCompleted = true;
            console.log("Slide completed!");
            if (!this.hasTriggered) {
                this.hasTriggered = true;
                this.$emit('onSlideCompleted');

                // Auto reset after a delay
                if (this.autoReset) {
                    setTimeout(() => {
                        this.resetSlider();
                    }, 1500);
                }
            }
        },

        resetSlider() {
            this.sliderPosition = 0;
            this.isCompleted = false;
            this.hasTriggered = false;
        }
    }
};
</script>

<style lang="scss" scoped>
.slide-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    padding: 10px 20px;
}

.slide-button {
    position: relative;
    display: inline-block;
    transition: all 0.3s ease;

    &:hover:not(.slide-button--disabled):not(.slide-button--completed) {
        transform: translateY(-1px);
        filter: drop-shadow(0 4px 8px rgba(23, 55, 127, 0.2));
    }

    &--disabled {
        opacity: 0.6;
        cursor: not-allowed;
        pointer-events: none;
    }

    &--completed {
        .slide-button__container {
            background: #17377F;
            border-color: #17377F;
        }
        
        .slide-button__text {
            color: white !important;
        }
    }
}

.slide-button__container {
    position: relative;
    display: flex;
    align-items: center;
    width: 271px;
    height: 53px;
    background: #E3E8F3;
    border: 1px solid #17377F;
    border-radius: 50px;
    padding: 8px;
    overflow: hidden;
    cursor: grab;
    user-select: none;
    
    &:active {
        cursor: grabbing;
    }
}

.slide-button__success-overlay {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    background: linear-gradient(300deg, #17377F 30%, #144394 60%);
    border-radius: 50px;
    transition: width 0.3s ease;
    z-index: 1;
}

.slide-button__icon-wrapper {
    position: relative;
    z-index: 3;
    transition: transform 0.3s ease;
    will-change: transform;
}

.slide-button__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 37px;
    height: 37px;
    background: #17377F;
    border: 1px solid #17377F;
    border-radius: 50px;
    transition: all 0.2s ease;
    cursor: grab;
    
    &:active {
        cursor: grabbing;
    }
    
    .slide-button--completed & {
        background: white;
        border-color: white;
        
        .slide-button__chevron {
            fill: #17377F;
        }
    }
}

.slide-button__chevron {
    width: 19px;
    height: 19px;
    fill: white;
    transition: fill 0.2s ease;
}

.slide-button__text {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%) translateX(11px);
    font-size: 16px;
    font-weight: 400;
    color: #17377F;
    white-space: nowrap;
    line-height: 1;
    z-index: 2;
    transition: all 0.3s ease;
    pointer-events: none;
    
    &--faded {
        opacity: 0.3;
    }
}

// Responsive adjustments
@media (max-width: 320px) {
    .slide-button__container {
        width: 240px;
        height: 48px;
    }
    
    .slide-button__icon {
        width: 32px;
        height: 32px;
    }
    
    .slide-button__chevron {
        width: 16px;
        height: 16px;
    }
    
    .slide-button__text {
        font-size: 16px;
    }
}

// Smooth animation for auto-reset
.slide-button__icon-wrapper {
    transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}
</style>
